import React, { useCallback, useEffect, useState, useRef } from 'react';
import moment from 'moment';
import styles from './DueDatePickerPopover.less';
import classNames from 'classnames';
import { forTemplate } from '@trello/i18n';
import {
  Popover,
  PopoverScreen,
  PopoverProps,
  usePopover,
} from '@trello/nachos/popover';
import { Select } from '@trello/nachos/select';
import { Button } from '@trello/nachos/button';
import { useDatePickerMutation } from './DatePickerMutation.generated';
import { importWithRetry } from '@trello/use-lazy-component';
import l from 'app/scripts/lib/localize';
import { Spinner } from '@trello/nachos/spinner';
import { TrelloStorage } from '@trello/storage';
import { getLastMemberId } from 'app/gamma/src/util/last-session';
import { Analytics } from '@trello/atlassian-analytics';

const LAST_REMINDER_USED_KEY_PREFIX = 'lastDueDateReminder';

const format = forTemplate('due_date_picker');

enum Screen {
  DueDatePickerScreen,
}

interface DueDatePickerScreenProps {
  due?: string | null;
  dueReminder?: number | null;
  idCard: string;
  idOrg?: string;
  hidePopover: () => void;
}

interface DueDatePickerProps extends DueDatePickerScreenProps {
  popProps: PopoverProps;
}

interface SelectOption {
  value: string;
  label: string;
}

function getPikadayStrings() {
  return {
    previousMonth: l('prev month button text'),
    nextMonth: l('next month button text'),
    months: moment.months(),
    weekdays: moment.weekdays(),
    weekdaysShort: moment.weekdaysMin(),
  };
}

const timeFormat = 'LT';
const parseTimeFormat = [timeFormat, 'h:mm A'];
const isTimeStringValid = (userTimeInput: string) =>
  moment(userTimeInput, parseTimeFormat).isValid();

export const useDatePickerPopover = () => {
  return usePopover<HTMLDivElement>({
    initialScreen: Screen.DueDatePickerScreen,
  });
};

export const getLastReminderKey = () => {
  const idMe = getLastMemberId();

  return `${LAST_REMINDER_USED_KEY_PREFIX}_${idMe}`;
};

export const DueDatePickerPopover: React.FunctionComponent<DueDatePickerProps> = ({
  due,
  dueReminder,
  idCard,
  idOrg,
  popProps,
  hidePopover,
}) => {
  useEffect(() => {
    if (popProps.isVisible) {
      Analytics.sendScreenEvent({
        name: 'datePickerInlineDialog',
        containers: {
          card: {
            id: idCard,
          },
          organization: {
            id: idOrg,
          },
        },
      });
    }
  }, [popProps.isVisible, idCard, idOrg]);

  return (
    <Popover {...popProps} title={format('change-due-date')}>
      <DueDatePickerPopoverScreen
        due={due}
        dueReminder={dueReminder}
        idCard={idCard}
        idOrg={idOrg}
        hidePopover={hidePopover}
      />
    </Popover>
  );
};

export const DueDatePickerPopoverScreen: React.FunctionComponent<DueDatePickerScreenProps> = ({
  due,
  dueReminder,
  idCard,
  idOrg,
  hidePopover,
}) => {
  const lastReminderUsed = JSON.parse(TrelloStorage.get(getLastReminderKey()));

  const initialDueReminder: number =
    dueReminder !== null && dueReminder !== undefined
      ? dueReminder
      : lastReminderUsed !== null
      ? lastReminderUsed
      : 1440;

  const initialDueDate = due ? due : moment().toString();
  const initialDueTime = due
    ? moment(due).format(timeFormat)
    : moment().hours(12).minutes(0).format(timeFormat);

  const [changeDueDate] = useDatePickerMutation();
  const [selectedDueReminder, setSelectedDueReminder] = useState(
    initialDueReminder,
  );
  const [loading, setLoading] = useState(true);
  const [field, setField] = useState<HTMLInputElement | null>(null);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [selectedDate, setSelectedDate] = useState(initialDueDate);
  const [time, setTime] = useState(initialDueTime);
  const pikadayMounted = useRef(false);

  const setDueDate = useCallback(
    (targetDue: string | null) => {
      hidePopover();

      Analytics.sendClickedButtonEvent({
        buttonName: targetDue ? 'saveDateButton' : 'removeDateButton',
        source: 'datePickerInlineDialog',
        containers: {
          card: {
            id: idCard,
          },
          organization: {
            id: idOrg,
          },
        },
      });

      TrelloStorage.set(getLastReminderKey(), selectedDueReminder);

      const traceId = Analytics.startTask({
        taskName: 'edit-card/due',
        source: 'datePickerInlineDialog',
      });

      try {
        changeDueDate({
          variables: {
            traceId,
            cardId: idCard,
            due: targetDue,
            dueReminder: targetDue ? selectedDueReminder.toString() : '-1',
          },
        });

        Analytics.sendTrackEvent({
          action: targetDue ? 'set' : 'removed',
          actionSubject: 'dueDate',
          source: 'datePickerInlineDialog',
          containers: {
            card: {
              id: idCard,
            },
            organization: {
              id: idOrg,
            },
          },
        });

        Analytics.taskSucceeded({
          taskName: 'edit-card/due',
          traceId,
          source: 'datePickerInlineDialog',
        });
      } catch (error) {
        throw Analytics.taskFailed({
          taskName: 'edit-card/due',
          traceId,
          source: 'datePickerInlineDialog',
          error,
        });
      }
    },
    [idCard, idOrg, selectedDueReminder, hidePopover, changeDueDate],
  );

  const validateAndSetTime = useCallback(() => {
    if (isTimeStringValid(time)) {
      setTime(moment(time, parseTimeFormat).format(timeFormat));
    } else {
      setTime(moment().hours(12).minutes(0).format(timeFormat));
    }
  }, [time]);

  const setSelectedDueDateMoment = useCallback(() => {
    if (selectedDate) {
      const timeMoment = moment(time, parseTimeFormat);

      const dueAndTimeMoment = moment(selectedDate)
        .hour(timeMoment.hour())
        .minute(timeMoment.minute());

      const dueTimeString = dueAndTimeMoment.toISOString();

      return setDueDate(dueTimeString);
    }
  }, [time, selectedDate, setDueDate]);

  useEffect(() => {
    const selectDay = (date: Date) => {
      Analytics.sendUIEvent({
        action: 'selected',
        actionSubject: 'dueDate',
        source: 'datePickerInlineDialog',
        containers: {
          card: {
            id: idCard,
          },
          organization: {
            id: idOrg,
          },
        },
      });
      setSelectedDate(date.toISOString());
    };

    if (field && container) {
      if (!pikadayMounted.current) {
        pikadayMounted.current = true;
        importWithRetry(
          () => import(/* webpackChunkName: "pikaday" */ 'pikaday'),
        ).then(({ default: Pikaday }) => {
          new Pikaday({
            field,
            container,
            bound: false,
            format: 'l',
            firstDay: moment.localeData().firstDayOfWeek(),
            i18n: getPikadayStrings(),
            keyboardInput: false,
            onSelect: selectDay,
            defaultDate: due ? new Date(due) : moment().toDate(),
            setDefaultDate: true,
          });
          setLoading(false);
        });
      }
    }
  }, [field, container, due, idCard, idOrg]);

  const reminderOptions = [
    { value: '-1', label: format('none') },
    { value: '0', label: format('at-time') },
    { value: '5', label: format('five-min-before-remind') },
    { value: '10', label: format('ten-min-before-remind') },
    { value: '15', label: format('fifteen-min-before-remind') },
    { value: '60', label: format('one-hour-before-remind') },
    { value: '120', label: format('two-hours-before-remind') },
    { value: '1440', label: format('one-day-before-remind') },
    { value: '2880', label: format('two-days-before-remind') },
  ];
  return (
    <PopoverScreen id={Screen.DueDatePickerScreen}>
      <div className={styles.inputFields}>
        <div className={styles.selectDate}>
          <label>
            {format('date')}
            <input
              className="date"
              ref={setField}
              placeholder={format('enter-date')}
            />
          </label>
        </div>
        <div className={styles.selectTime}>
          <label>
            {format('time')}
            <input
              className="time"
              value={time}
              // eslint-disable-next-line react/jsx-no-bind
              onChange={(e) => setTime(e.target.value)}
              // eslint-disable-next-line react/jsx-no-bind
              onBlur={() => validateAndSetTime()}
              placeholder={format('enter-time')}
            />
          </label>
        </div>
      </div>
      <div ref={setContainer} className={styles.pikaday} role="button" />
      {loading && <Spinner centered />}
      <label>
        {format('set-reminder')}
        <Select
          options={reminderOptions}
          // eslint-disable-next-line react/jsx-no-bind
          onChange={({ value }: SelectOption) =>
            setSelectedDueReminder(parseInt(value, 10))
          }
          defaultValue={reminderOptions.filter(
            (option) => parseInt(option.value, 10) === selectedDueReminder,
          )}
          className={'dueReminderSelect'}
        />
      </label>
      <div className={styles.reminderClarification}>
        {format('reminder-clarification')}
      </div>
      <div className={styles.buttons}>
        <Button
          appearance="primary"
          // eslint-disable-next-line react/jsx-no-bind
          onClick={() => setSelectedDueDateMoment()}
          isDisabled={!selectedDate}
          className={'saveDateButton'}
        >
          {format('save')}
        </Button>
        <Button
          appearance="danger"
          // eslint-disable-next-line react/jsx-no-bind
          onClick={() => setDueDate(null)}
          isDisabled={!due}
          className={classNames(styles.removeDateButton, 'removeDateButton')}
        >
          {format('remove')}
        </Button>
      </div>
    </PopoverScreen>
  );
};
